import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useDirection, useI18n } from '../i18n';
import { cn } from '../lib/cn';

export interface PaginationProps {
  page: number;
  totalPages: number;
  /** Builds the href for a page. Real links, so the list works without JS. */
  hrefFor: (page: number) => string;
  onNavigate?: (page: number) => void;
  className?: string;
}

/** How many numbered pages to show around the current one. */
const WINDOW = 1;

/**
 * Page navigation.
 *
 * Real `<a>` elements, not buttons. The portfolio listing has to work with
 * JavaScript disabled (FR-PORT), which means page two is a URL, and a URL is a
 * link — one that can also be middle-clicked, bookmarked and crawled.
 *
 * **Direction.** Previous and next are *logical*, not left and right. In Arabic
 * the previous page sits on the right, so the chevron glyph is chosen by
 * direction. The order of the elements is not reversed in the markup: the
 * document direction already does that, and reversing it as well flips it back.
 * This is the mistake ADR-013 exists to prevent.
 *
 * **Numbers.** Page numbers go through `formatNumber`, so an Arabic reader sees
 * Arabic-Indic digits if that is what their locale uses. A hard-coded `{page}`
 * renders Latin digits inside otherwise Arabic text.
 */
export function Pagination({ page, totalPages, hrefFor, onNavigate, className }: PaginationProps) {
  const { t, formatNumber } = useI18n();
  const dir = useDirection();

  if (totalPages <= 1) return null;

  const PreviousIcon = dir === 'rtl' ? ChevronRight : ChevronLeft;
  const NextIcon = dir === 'rtl' ? ChevronLeft : ChevronRight;

  const pages = pageWindow(page, totalPages);

  return (
    <nav aria-label={t('common.pagination.label')} className={cn('flex justify-center', className)}>
      <ul className="flex items-center gap-1">
        <li>
          <Step
            disabled={page <= 1}
            href={hrefFor(page - 1)}
            onNavigate={() => onNavigate?.(page - 1)}
            label={t('common.actions.previous')}
          >
            <PreviousIcon className="size-4" aria-hidden="true" />
          </Step>
        </li>

        {pages.map((entry, index) =>
          entry === 'gap' ? (
            // Presentational: a screen reader announcing "ellipsis" between
            // page numbers adds nothing a listener can act on.
            <li key={`gap-${index}`} aria-hidden="true" className="px-2 text-text-subtle">
              …
            </li>
          ) : (
            <li key={entry}>
              <a
                href={hrefFor(entry)}
                onClick={() => onNavigate?.(entry)}
                aria-current={entry === page ? 'page' : undefined}
                aria-label={t('common.pagination.goToPage', { page: formatNumber(entry) })}
                className={cn(
                  'flex min-w-9 items-center justify-center rounded-full px-2 py-1.5 text-sm transition-colors',
                  entry === page
                    ? 'bg-accent font-medium text-accent-fg'
                    : 'text-text-muted hover:bg-surface-hover hover:text-text',
                )}
              >
                {formatNumber(entry)}
              </a>
            </li>
          ),
        )}

        <li>
          <Step
            disabled={page >= totalPages}
            href={hrefFor(page + 1)}
            onNavigate={() => onNavigate?.(page + 1)}
            label={t('common.actions.next')}
          >
            <NextIcon className="size-4" aria-hidden="true" />
          </Step>
        </li>
      </ul>
    </nav>
  );
}

/**
 * A previous/next control.
 *
 * When there is nowhere to go it renders a `<span>` with `aria-disabled`, not a
 * disabled link — `disabled` is not a valid attribute on an anchor, and an
 * anchor without `href` is removed from the tab order entirely, so a keyboard
 * user tabbing through the pager would find the controls silently disappearing
 * as they reach the last page.
 */
function Step({
  disabled,
  href,
  onNavigate,
  label,
  children,
}: {
  disabled: boolean;
  href: string;
  onNavigate: () => void;
  label: string;
  children: React.ReactNode;
}) {
  const shared = 'flex items-center justify-center rounded-full p-2 transition-colors';

  if (disabled) {
    return (
      <span aria-disabled="true" className={cn(shared, 'text-text-subtle opacity-50')}>
        {children}
        <span className="hs-sr-only">{label}</span>
      </span>
    );
  }

  return (
    <a
      href={href}
      onClick={onNavigate}
      className={cn(shared, 'text-text-muted hover:bg-surface-hover hover:text-text')}
    >
      {children}
      <span className="hs-sr-only">{label}</span>
    </a>
  );
}

/**
 * The pages to render: always the first and last, plus a window around the
 * current one, with gaps standing in for the rest.
 *
 * Exported for its own test — off-by-one errors here produce a pager that skips
 * a page, which nobody notices until content is missing from a listing.
 */
export function pageWindow(page: number, totalPages: number): (number | 'gap')[] {
  const shown = new Set<number>([1, totalPages]);

  for (let candidate = page - WINDOW; candidate <= page + WINDOW; candidate += 1) {
    if (candidate >= 1 && candidate <= totalPages) shown.add(candidate);
  }

  const ordered = [...shown].sort((a, b) => a - b);
  const output: (number | 'gap')[] = [];

  for (const [index, value] of ordered.entries()) {
    const previous = ordered[index - 1];
    // A gap standing in for exactly one page is longer than the page number it
    // hides, so render the number instead.
    if (previous !== undefined && value - previous > 1) {
      output.push(value - previous === 2 ? value - 1 : 'gap');
    }
    output.push(value);
  }

  return output;
}
